<template>
  <div class="history-page">

    <!-- HEADER -->
    <div class="history-header">
      <h2>{{ $t('profile.gamehistory') }}</h2>

      <div class="page-size">
        <span>{{ $t('profile.perpage') }}</span>
        <select v-model.number="pagination.pageSize" @change="reload">
          <option :value="10">10</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
        </select>
      </div>
    </div>

    <!-- LOADING -->
    <div v-if="loading" class="center">
      <div class="spinner"></div>
    </div>

    <!-- EMPTY -->
    <div v-else-if="gameHistory.length === 0" class="empty">
      🎮 {{ $t('profile.No bets found') }}
    </div>

    <!-- DESKTOP -->
    <div v-else class="desktop-only history-box">
      <table class="history-table">
        <thead>
          <tr>
            <th>Game</th>
            <th>Bet</th>
            <th>Win</th>
            <th>X</th>
            <th>Balance</th>
            <th>Date</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="item in gameHistory"
            :key="item.round_id"
            class="history-row"
          >
            <td class="game-cell">
              <img :src="item.banner" />
              <div>
                <div class="name">{{ item.game_name }}</div>
                <div class="type">{{ item.game_type }}</div>
              </div>
            </td>

            <td>
              <span class="bet" v-if="item.bet_money > 0">
                -{{ format(item.bet_money) }}
              </span>
              <span v-else class="muted">0</span>
            </td>

            <td>
              <span class="win" v-if="item.win_money > 0">
                +{{ format(item.win_money) }}
              </span>
              <span v-else class="muted">0</span>
            </td>

            <td>
              <span class="x" v-if="item.multiplier > 0">
                {{ item.multiplier }}x
              </span>
              <span v-else class="muted">0x</span>
            </td>

            <td>{{ format(item.balance_after) }}</td>
            <td class="date">{{ date(item.created_at) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- MOBILE -->
    <div class="mobile-only">
      <div
        v-for="item in gameHistory"
        :key="item.round_id"
        class="history-card"
      >
        <div class="top">
          <img :src="item.banner" />
          <div class="info">
            <div class="name">{{ item.game_name }}</div>
            <div class="type">{{ item.game_type }}</div>
          </div>
          <div class="x" v-if="item.multiplier > 0">
            {{ item.multiplier }}x
          </div>
        </div>

        <div class="stats">
          <div>
            <span>Bet:</span>
            <strong class="bet">-{{ format(item.bet_money) }}</strong>
          </div>
          <div>
            <span>Win:</span>
            <strong class="win">+{{ format(item.win_money) }}</strong>
          </div>
          <div>
            <span>Balance:</span>
            <strong>{{ format(item.balance_after) }}</strong>
          </div>
        </div>

        <div class="card-date">{{ date(item.created_at) }}</div>
      </div>
    </div>

    <!-- PAGINATION -->
    <div class="pagination">
      <button @click="prev" :disabled="pagination.currentPage === 1">‹</button>
      <span>{{ pagination.currentPage }} / {{ pagination.totalPages }}</span>
      <button @click="next" :disabled="pagination.currentPage === pagination.totalPages">›</button>
    </div>

  </div>
</template>


<script>
import { mapGetters } from "vuex";
import axios from "axios";

export default {
  data() {
    return {
      gameHistory: [],
      loading: true,
      pagination: {
        pageSize: 20,
        currentPage: 1,
        totalPages: 1
      }
    };
  },
  computed: {
    ...mapGetters(["authUser"])
  },
  mounted() {
    this.fetch();
  },
  methods: {
    async fetch() {
      this.loading = true;
      const id = this.authUser.user._id || this.authUser.user.phone;

      const res = await axios.get(
        `/game-history/${id}?page=${this.pagination.currentPage}&limit=${this.pagination.pageSize}`
      );

      this.gameHistory = res.data.history;
      this.pagination = res.data.pagination;
      this.loading = false;
    },
    reload() {
      this.pagination.currentPage = 1;
      this.fetch();
    },
    prev() {
      if (this.pagination.currentPage > 1) {
        this.pagination.currentPage--;
        this.fetch();
      }
    },
    next() {
      if (this.pagination.currentPage < this.pagination.totalPages) {
        this.pagination.currentPage++;
        this.fetch();
      }
    },
    format(v) {
      return Number(v || 0).toFixed(2);
    },
    date(d) {
      return new Date(d).toLocaleString("tr-TR");
    }
  }
};
</script>

<style scoped>
 .history-page {
  padding: 16px;
  color: #fff;
}

/* HEADER */
.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  margin-bottom: 18px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.history-header h2 {
  font-size: 1.6rem;
  font-weight: 700;
}

.page-size select {
  background: var(--active-button);
  color: #fff;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.15);
  padding: 4px 8px;
}

/* TABLE CONTAINER */
.history-box {
  background: var(--background1);
  border-radius: 14px;
  overflow: hidden;
}

/* TABLE */
.history-table {
  width: 100%;
  border-collapse: collapse;
}

.history-table thead {
  background: linear-gradient(
    to right,
    rgba(1,224,163,0.08),
    rgba(1,224,163,0.02)
  );
}

.history-table th {
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #b3bec1;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.history-row {
  transition: background .2s ease;
}

 

.history-row td {
  padding: 16px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}

/* GAME CELL */
.game-cell {
  display: flex;
  gap: 12px;
  align-items: center;
}

.game-cell img {
  width: 72px;
  border-radius: 8px;
}

.name {
  font-weight: 600;
}

.type {
  font-size: 12px;
  color: #aaa;
}

/* VALUES */
.bet { color: #ff5252; font-weight: 600; }
.win { color: #01e0a3; font-weight: 600; }

.x {
  background: rgba(255,215,0,.15);
  color: #ffd700;
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 700;
}

.muted { color: #000; }

.date {
  font-size: 12px;
  color: #888;
}

/* MOBILE */
.mobile-only { display: none; }

.history-card {
  background: var(--background1);
  border-radius: 16px;
  padding: 14px;
  margin-bottom: 14px;
  border: 1px solid rgba(255,255,255,0.06);
}

.history-card .top {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}

.history-card img {
  width: 56px;
  border-radius: 10px;
}

.stats {
  display: grid;
  grid-template-columns: repeat(3,1fr);
  margin-top: 12px;
  gap: 8px;
}

.stats span {
  font-size: 12px;
  color: #aaa;
}

.card-date {
  margin-top: 10px;
  font-size: 12px;
  color: #777;
  text-align: right;
}

/* PAGINATION */
.pagination {
  display: flex;
  justify-content: center;
  gap: 14px;
  margin-top: 20px;
}

.pagination button {
  background: var(--active-button);
  border: 1px solid rgba(255,255,255,0.1);
  color: #fff;
  border-radius: 8px;
  width: 36px;
  height: 36px;
}

/* RESPONSIVE */
.desktop-only { display: block; }

@media (max-width: 768px) {
  .desktop-only { display: none; }
  .mobile-only { display: block; }
}

</style>
